/**
 * Freeze V2 — Temp Embryo Grid (raw-table prototype)
 *
 * Lightweight HTML-table replacement for the ag-grid detail grid to cut the
 * cost of mounting nested grids. Selection is controlled directly from
 * FreezeSelectionContext; edits reuse the same REST mutations + validation as
 * the ag-grid path.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import {
  DataTable,
  EditableCell,
  NUM_INPUT,
  TEXT_INPUT,
} from '@/components/common/data-table';
import type { ColumnDef } from '@/components/common/data-table';
import { TempEmbryoLink } from '@/components/common/links/TempEmbryoLink';
import type {
  FertAndEmbryosResponse,
  TempEmbryoResponse,
} from '@/services/freeze';

import {
  useFreezeForm,
  useFreezeGrid,
  useFreezeSelection,
} from '../../contexts';
import { useFertRowSelection } from '../../hooks/useFertRowSelection';
import { EMBRYO_QUERY_KEY } from '../../hooks/useFreezeData';
import { useUpdateInterimEmbryoFieldMutation } from '../../services/freezeApi';
import { caneStyle, freezeMethodStyle } from '../../utils/cellStyles';
import { TOG } from '../../utils/columnDefs/fertilizationCols';
import { FIELD_NAMES } from '../../utils/constants';
import { applyRangeSelection } from '../../utils/rangeSelect';

// Temp (not yet in OvaDrive) rows are tinted amber so they read differently from live embryos.
const TEMP_ROW_CLASS = 'odd:bg-amber-50 even:bg-amber-100/70';

type TempEmbryoGridProps = {
  tempEmbryos: TempEmbryoResponse[];
  /** Live embryo ids for the same fert, so parent selection accounts for both tables. */
  siblingLiveIds: string[];
  isExport: boolean;
  fertilizationId: string;
  billAccountCaningOption: number | null;
  onRefresh: () => void;
};

export const TempEmbryoGrid = memo(function TempEmbryoGrid({
  tempEmbryos,
  siblingLiveIds,
  isExport,
  fertilizationId,
  billAccountCaningOption,
  onRefresh,
}: TempEmbryoGridProps) {
  const reduxDispatch = useDispatch();
  const queryClient = useQueryClient();
  const { isUpdateRunning } = useFreezeGrid();
  const { batchForm } = useFreezeForm();
  const { dispatch, tempEmbryoIds, liveEmbryoIds } = useFreezeSelection();
  const { mutateAsync: updateTempEmbryo } =
    useUpdateInterimEmbryoFieldMutation();
  const [rows, setRows] = useState(tempEmbryos);
  useEffect(() => setRows(tempEmbryos), [tempEmbryos]);

  const patch = useCallback(
    (id: string | null, partial: Partial<TempEmbryoResponse>) => {
      if (!id) return;
      setRows((rs) =>
        rs.map((r) =>
          r.embryoFreezeEntryId === id ? { ...r, ...partial } : r,
        ),
      );
      /**
       * Write the same change back into the shared embryo cache.
       *
       * The field mutation doesn't invalidate anything (invalidating per
       * keystroke would refetch every fert on the day), so without this the
       * cache keeps the pre-edit row. FreezeActionBar validates against that
       * cache, which is why updating temp embryos warned "missing Stage and
       * Grade" for values the tech had just typed — and why the update itself
       * still went through correctly (WI 37941 #11).
       */
      queryClient.setQueriesData<FertAndEmbryosResponse[]>(
        { queryKey: [EMBRYO_QUERY_KEY] },
        (data) =>
          data?.map((entry) =>
            entry.fertId !== fertilizationId
              ? entry
              : {
                  ...entry,
                  tempEmbryos: entry.tempEmbryos?.map((e) =>
                    e.embryoFreezeEntryId === id ? { ...e, ...partial } : e,
                  ),
                },
          ),
      );
    },
    [queryClient, fertilizationId],
  );

  const ownIds = useMemo(
    () =>
      rows.map((e) => e.embryoFreezeEntryId).filter((id): id is string => !!id),
    [rows],
  );

  const selectedSet = useMemo(() => new Set(tempEmbryoIds), [tempEmbryoIds]);
  const allSelected =
    ownIds.length > 0 && ownIds.every((id) => selectedSet.has(id));

  const syncParentFert = useFertRowSelection({
    fertilizationId,
    ownIds,
    siblingIds: siblingLiveIds,
    siblingSelectedIds: liveEmbryoIds,
  });

  const orderedIdsRef = useRef<string[]>([]);
  const anchorIdRef = useRef<string | null>(null);
  const handleSortedRowsChange = useCallback((sorted: typeof rows) => {
    orderedIdsRef.current = sorted
      .map((r) => r.embryoFreezeEntryId)
      .filter((id): id is string => !!id);
  }, []);

  const toggleRow = useCallback(
    (id: string, checked: boolean, shiftKey = false) => {
      const next = shiftKey
        ? applyRangeSelection({
            orderedIds: orderedIdsRef.current,
            selectedIds: tempEmbryoIds,
            anchorId: anchorIdRef.current,
            targetId: id,
            checked,
          })
        : checked
          ? [...tempEmbryoIds, id]
          : tempEmbryoIds.filter((x) => x !== id);
      anchorIdRef.current = id;
      dispatch({ type: 'SET_TEMP_IDS', ids: next });
      syncParentFert(next);
    },
    [tempEmbryoIds, dispatch, syncParentFert],
  );

  const toggleAll = useCallback(
    (checked: boolean) => {
      const ownSet = new Set(ownIds);
      const next = checked
        ? Array.from(new Set([...tempEmbryoIds, ...ownIds]))
        : tempEmbryoIds.filter((id) => !ownSet.has(id));
      dispatch({ type: 'SET_TEMP_IDS', ids: next });
      syncParentFert(next);
    },
    [ownIds, tempEmbryoIds, dispatch, syncParentFert],
  );

  const save = useCallback(
    async (id: string | null, field: string, value: string | null) => {
      if (!id) return;
      try {
        await updateTempEmbryo({ id, field, value });
      } catch (error) {
        reduxDispatch(
          addToast({
            severity: 'error',
            summary: 'Save Failed',
            detail: `Could not save temp embryo field: ${error}`,
            life: 3000,
          }),
        );
      }
    },
    [updateTempEmbryo, reduxDispatch],
  );

  const invalid = useCallback(
    (summary: string, detail: string) => {
      reduxDispatch(
        addToast({ severity: 'error', summary, detail, closable: true }),
      );
    },
    [reduxDispatch],
  );

  const editStageGrade = useCallback(
    (
      row: TempEmbryoResponse,
      field: 'stage' | 'grade',
      value: string,
    ): boolean => {
      if (value) {
        if (!batchForm.freezeDay || !batchForm.graderId) {
          invalid(
            'Missing Setup',
            'Freeze Day and Grader must be selected before editing stage/grade.',
          );
          return false;
        }
        const n = parseInt(value, 10);
        if (field === 'grade' && (n < 1 || n > 3)) {
          invalid('Invalid Grade', 'Grade must be between 1 and 3');
          return false;
        }
        if (field === 'stage' && (n < 3 || n > 9)) {
          invalid('Invalid Stage', 'Stage must be between 3 and 9');
          return false;
        }
        patch(row.embryoFreezeEntryId, { [field]: n });
        save(row.embryoFreezeEntryId, field, value);
        if (field === 'grade' && n === 3) {
          patch(row.embryoFreezeEntryId, { lowQuality: true });
          save(row.embryoFreezeEntryId, FIELD_NAMES.LG, 'true');
        }
        if (field === 'stage' && n >= 8) {
          patch(row.embryoFreezeEntryId, { crackedZona: true });
          save(row.embryoFreezeEntryId, 'crackedZona', 'true');
        }
        // Parity with v1: filling in stage/grade auto-checks the row so techs
        // don't have to click the checkbox separately.
        if (
          row.embryoFreezeEntryId &&
          !selectedSet.has(row.embryoFreezeEntryId)
        ) {
          toggleRow(row.embryoFreezeEntryId, true);
        }
        return true;
      }
      patch(row.embryoFreezeEntryId, { [field]: null });
      save(row.embryoFreezeEntryId, field, null);
      return true;
    },
    [
      batchForm.freezeDay,
      batchForm.graderId,
      invalid,
      save,
      patch,
      selectedSet,
      toggleRow,
    ],
  );

  const editDay = useCallback(
    (row: TempEmbryoResponse, day: 6.5 | 7, checked: boolean) => {
      patch(row.embryoFreezeEntryId, {
        d65: day === 6.5 && checked ? 1 : 0,
        d7: day === 7 && checked ? 1 : 0,
      });
      save(
        row.embryoFreezeEntryId,
        FIELD_NAMES.FREEZE_TIME,
        checked ? String(day) : null,
      ).then(onRefresh);
    },
    [save, onRefresh, patch],
  );

  const headerTtAllChecked = rows.length > 0 && rows.every((e) => !!e.tt);

  const toggleAllTt = useCallback(
    (checked: boolean) => {
      for (const row of rows) {
        if (row.stage && row.grade) {
          patch(row.embryoFreezeEntryId, { tt: checked });
          save(row.embryoFreezeEntryId, FIELD_NAMES.TT, String(checked));
        }
      }
    },
    [rows, save, patch],
  );

  const columns = useMemo<ColumnDef<TempEmbryoResponse>[]>(
    () => [
      {
        key: 'select',
        width: 36,
        reorderable: false,
        hideable: false,
        chooserLabel: 'Select',
        header: (
          <input
            type="checkbox"
            aria-label="Select all temp embryos"
            checked={allSelected}
            onChange={(e) => toggleAll(e.target.checked)}
          />
        ),
        render: (row) => {
          const id = row.embryoFreezeEntryId ?? '';
          return (
            <input
              type="checkbox"
              checked={selectedSet.has(id)}
              readOnly
              onClick={(e) =>
                toggleRow(id, e.currentTarget.checked, e.shiftKey)
              }
            />
          );
        },
      },
      {
        key: 'embryo',
        header: 'Embryo',
        width: 130,
        hideable: false,
        render: (row) => (
          <span className="font-bold">
            <TempEmbryoLink
              embryoId={row.embryoFreezeEntryId ?? ''}
              embryoName={row.name ?? ''}
            />
          </span>
        ),
      },
      {
        key: 'straw',
        header: 'Straw',
        width: 60,
        align: 'right',
        editable: true,
        sortable: true,
        sortValue: (row) => row.straw,
        render: (row) => {
          const id = row.embryoFreezeEntryId ?? '';
          return (
            <EditableCell
              value={row.straw}
              type="number"
              className={NUM_INPUT}
              disabled={isUpdateRunning}
              onCommit={(v) => {
                patch(id, { straw: v ? Number(v) : null });
                save(id, 'straw', v || null);
              }}
            />
          );
        },
      },
      {
        key: 'stage',
        header: 'Stage',
        width: 60,
        align: 'right',
        editable: true,
        render: (row) => (
          <EditableCell
            value={row.stage}
            type="integer"
            aria-label="Stage"
            className={NUM_INPUT}
            disabled={isUpdateRunning}
            onCommit={(v) => editStageGrade(row, 'stage', v)}
          />
        ),
      },
      {
        key: 'grade',
        header: 'Grade',
        width: 60,
        align: 'right',
        editable: true,
        render: (row) => (
          <EditableCell
            value={row.grade}
            type="integer"
            aria-label="Grade"
            className={NUM_INPUT}
            disabled={isUpdateRunning}
            onCommit={(v) => editStageGrade(row, 'grade', v)}
          />
        ),
      },
      {
        key: 'lg',
        header: 'LG',
        width: 42,
        align: 'center',
        render: (row) => {
          const id = row.embryoFreezeEntryId ?? '';
          return (
            <input
              type="checkbox"
              checked={!!row.lowQuality}
              disabled={isUpdateRunning}
              onChange={(e) => {
                patch(id, { lowQuality: e.target.checked });
                save(id, FIELD_NAMES.LG, String(e.target.checked));
              }}
            />
          );
        },
      },
      {
        key: 'dc',
        header: 'DC',
        width: 42,
        align: 'center',
        render: (row) => {
          const id = row.embryoFreezeEntryId ?? '';
          return (
            <input
              type="checkbox"
              checked={row.discard === 1}
              disabled={isUpdateRunning}
              onChange={(e) => {
                patch(id, { discard: e.target.checked ? 1 : 0 });
                save(id, FIELD_NAMES.DC, String(e.target.checked));
              }}
            />
          );
        },
      },
      {
        key: 'cane',
        header: 'Cane',
        /* Matches LiveEmbryoGrid -- 76px clipped the cane suffix (WI 37941 #4). */
        width: 96,
        editable: true,
        cellStyle: () => caneStyle(billAccountCaningOption),
        render: (row) => {
          const id = row.embryoFreezeEntryId ?? '';
          return (
            <EditableCell
              value={row.cane}
              type="text"
              /* Bold to match LiveEmbryoGrid - see the note there on why the
                 <td>'s font-weight does not reach the input. */
              className={`${TEXT_INPUT} w-full font-bold`}
              disabled={isUpdateRunning}
              onCommit={(v) => {
                patch(id, { cane: v || null });
                save(id, 'cane', v || null);
              }}
            />
          );
        },
      },
      {
        key: 'tt',
        width: 64,
        align: 'center',
        headerTitle: 'Stage and grade required',
        chooserLabel: 'T/T',
        header: (
          <span className="inline-flex items-center gap-1">
            T/T
            <input
              type="checkbox"
              aria-label="Set T/T on all graded embryos"
              checked={headerTtAllChecked}
              disabled={isUpdateRunning}
              onChange={(e) => toggleAllTt(e.target.checked)}
            />
          </span>
        ),
        cellStyle: () => (isExport ? { backgroundColor: TOG.red } : undefined),
        render: (row) => {
          const id = row.embryoFreezeEntryId ?? '';
          return (
            <input
              type="checkbox"
              checked={!!row.tt}
              disabled={isUpdateRunning}
              onChange={(e) => {
                patch(id, { tt: e.target.checked });
                save(id, FIELD_NAMES.TT, String(e.target.checked));
              }}
            />
          );
        },
      },
      {
        key: 'cz',
        header: 'CZ',
        width: 42,
        align: 'center',
        render: (row) => {
          const id = row.embryoFreezeEntryId ?? '';
          return (
            <input
              type="checkbox"
              checked={!!row.crackedZona}
              disabled={isUpdateRunning}
              onChange={(e) => {
                patch(id, { crackedZona: e.target.checked });
                save(id, 'crackedZona', String(e.target.checked));
              }}
            />
          );
        },
      },
      {
        key: 'owner',
        cellClassName: 'whitespace-normal break-words',
        header: 'Owner',
        width: 110,
        render: (row) => row.ownerName,
      },
      {
        key: 'd65',
        header: '6.5',
        width: 42,
        align: 'center',
        render: (row) => (
          <input
            type="checkbox"
            checked={row.d65 === 1}
            disabled={isUpdateRunning}
            onChange={(e) => editDay(row, 6.5, e.target.checked)}
          />
        ),
      },
      {
        key: 'd7',
        header: '7',
        width: 42,
        align: 'center',
        render: (row) => (
          <input
            type="checkbox"
            checked={row.d7 === 1}
            disabled={isUpdateRunning}
            onChange={(e) => editDay(row, 7, e.target.checked)}
          />
        ),
      },
      {
        key: 'freezeMethod',
        cellClassName: 'whitespace-normal break-words',
        header: 'Freeze Method',
        width: 120,
        cellStyle: (row) => freezeMethodStyle(row.freezeMethodName),
        render: (row) => row.freezeMethodName,
      },
      {
        key: 'storageLoc',
        cellClassName: 'whitespace-normal break-words',
        header: 'Storage Loc',
        width: 130,
        render: (row) => row.storageLocationName,
      },
    ],
    [
      allSelected,
      selectedSet,
      toggleAll,
      toggleRow,
      isUpdateRunning,
      patch,
      save,
      editStageGrade,
      editDay,
      billAccountCaningOption,
      isExport,
      headerTtAllChecked,
      toggleAllTt,
    ],
  );

  return (
    <div className="flex flex-col gap-1">
      <span className="px-1 text-xs font-semibold text-gray-600">
        Temp Embryos ({tempEmbryos.length})
      </span>
      <DataTable
        rows={rows}
        columns={columns}
        className="text-sm"
        onSortedRowsChange={handleSortedRowsChange}
        rowClassName={() => TEMP_ROW_CLASS}
        getRowId={(row) => row.embryoFreezeEntryId ?? ''}
        storageKey="freeze-v2-temp-embryo-table"
        reorderable
        columnChooser
      />
    </div>
  );
});
